import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  getConfig,
  listProviders,
  updateAssistantConfig,
  type ProviderDefaults,
  type ProviderInfo,
  type SafeConfigResponse,
} from '@/lib/api';

const selectClass =
  'h-9 rounded-md border border-border bg-surface-elevated text-text-primary px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring [&>option]:bg-surface-elevated [&>option]:text-text-primary';

function AssistantDefaultsForm({ config }: { config: SafeConfigResponse }): React.ReactElement {
  const queryClient = useQueryClient();
  const { data: providers } = useQuery({
    queryKey: ['providers'],
    queryFn: listProviders,
    staleTime: 5 * 60 * 1000,
  });
  const [assistant, setAssistant] = useState<string>(config.assistant);
  const [assistantSettings, setAssistantSettings] = useState<Record<string, ProviderDefaults>>(
    config.assistants
  );
  const [saveMsg, setSaveMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    setAssistant(config.assistant);
    setAssistantSettings(config.assistants);
  }, [config]);

  const normalizedConfigSettings = JSON.stringify(config.assistants);
  const normalizedAssistantSettings = JSON.stringify(assistantSettings);
  const hasChanges =
    assistant !== config.assistant || normalizedAssistantSettings !== normalizedConfigSettings;

  function getProviderSettings(providerId: string): ProviderDefaults {
    return assistantSettings[providerId] ?? {};
  }

  function updateProviderSettings(providerId: string, updates: ProviderDefaults): void {
    setAssistantSettings(current => ({
      ...current,
      [providerId]: {
        ...(current[providerId] ?? {}),
        ...updates,
      },
    }));
  }

  const allProviderEntries: ProviderInfo[] = [];
  const providerIds = new Set<string>();
  for (const provider of providers ?? []) {
    allProviderEntries.push(provider);
    providerIds.add(provider.id);
  }
  for (const providerId of Object.keys(config.assistants)) {
    if (!providerIds.has(providerId)) {
      allProviderEntries.push({
        id: providerId,
        displayName: providerId,
        capabilities: {},
        builtIn: false,
      } satisfies ProviderInfo);
    }
  }

  const mutation = useMutation({
    mutationFn: updateAssistantConfig,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['config'] });
      setSaveMsg({ type: 'success', text: 'Settings saved.' });
      setTimeout(() => {
        setSaveMsg(null);
      }, 3000);
    },
    onError: (err: Error) => {
      setSaveMsg({ type: 'error', text: err.message });
    },
  });

  function handleSave(): void {
    mutation.mutate({
      assistant,
      assistants: assistantSettings,
    });
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-[140px_1fr] items-center gap-2 text-sm">
        <label htmlFor="default-assistant">Default Assistant</label>
        <select
          id="default-assistant"
          value={assistant}
          onChange={e => {
            setAssistant(e.target.value);
          }}
          className={selectClass}
        >
          {allProviderEntries.map(provider => (
            <option key={provider.id} value={provider.id}>
              {provider.displayName}
            </option>
          ))}
        </select>
      </div>

      <div className="space-y-4 border-t border-border pt-4">
        {allProviderEntries.map(provider => {
          const providerSettings = getProviderSettings(provider.id);

          if (provider.id === 'claude') {
            return (
              <div
                key={provider.id}
                className="grid grid-cols-[140px_1fr] items-center gap-2 text-sm"
              >
                <div className="font-medium">{provider.displayName}</div>
                <div className="text-muted-foreground">Built-in provider settings</div>

                <label htmlFor="claude-model">Model</label>
                <select
                  id="claude-model"
                  value={(providerSettings.model as string | undefined) ?? 'sonnet'}
                  onChange={e => {
                    updateProviderSettings('claude', { model: e.target.value });
                  }}
                  className={selectClass}
                >
                  <option value="sonnet">sonnet</option>
                  <option value="opus">opus</option>
                  <option value="haiku">haiku</option>
                </select>
              </div>
            );
          }

          if (provider.id === 'codex') {
            return (
              <div
                key={provider.id}
                className="grid grid-cols-[140px_1fr] items-center gap-2 text-sm"
              >
                <div className="font-medium">{provider.displayName}</div>
                <div className="text-muted-foreground">Built-in provider settings</div>

                <label htmlFor="codex-model">Model</label>
                <Input
                  id="codex-model"
                  value={(providerSettings.model as string | undefined) ?? ''}
                  onChange={e => {
                    updateProviderSettings('codex', { model: e.target.value });
                  }}
                  placeholder="gpt-5.3-codex"
                />

                <label htmlFor="reasoning">Reasoning Effort</label>
                <select
                  id="reasoning"
                  value={(providerSettings.modelReasoningEffort as string | undefined) ?? 'medium'}
                  onChange={e => {
                    updateProviderSettings('codex', {
                      modelReasoningEffort: e.target.value,
                    });
                  }}
                  className={selectClass}
                >
                  <option value="minimal">minimal</option>
                  <option value="low">low</option>
                  <option value="medium">medium</option>
                  <option value="high">high</option>
                  <option value="xhigh">xhigh</option>
                </select>

                <label htmlFor="web-search">Web Search</label>
                <select
                  id="web-search"
                  value={(providerSettings.webSearchMode as string | undefined) ?? 'disabled'}
                  onChange={e => {
                    updateProviderSettings('codex', { webSearchMode: e.target.value });
                  }}
                  className={selectClass}
                >
                  <option value="disabled">disabled</option>
                  <option value="cached">cached</option>
                  <option value="live">live</option>
                </select>
              </div>
            );
          }

          return (
            <div key={provider.id} className="rounded-md border border-border p-3 text-sm">
              <div className="font-medium">{provider.displayName}</div>
              <div className="mt-1 text-muted-foreground">
                Provider-specific settings are stored generically for this provider.
              </div>
              {Object.keys(providerSettings).length > 0 && (
                <pre className="mt-2 overflow-x-auto rounded bg-muted p-2 text-xs">
                  {JSON.stringify(providerSettings, null, 2)}
                </pre>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3">
        <Button onClick={handleSave} disabled={mutation.isPending || !hasChanges} size="sm">
          {mutation.isPending ? 'Saving...' : 'Save Changes'}
        </Button>
        {saveMsg && (
          <span
            className={`text-sm ${saveMsg.type === 'success' ? 'text-success' : 'text-destructive'}`}
          >
            {saveMsg.text}
          </span>
        )}
      </div>
    </div>
  );
}

export function AdminAssistantDefaultsCard(): React.ReactElement {
  const { data, isLoading, error } = useQuery({
    queryKey: ['config'],
    queryFn: getConfig,
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Assistant Defaults</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading && (
          <p className="text-sm text-muted-foreground">Loading assistant defaults...</p>
        )}
        {error && (
          <p className="text-sm text-destructive">
            {error instanceof Error ? error.message : 'Failed to load assistant defaults'}
          </p>
        )}
        {data && <AssistantDefaultsForm config={data.config} />}
      </CardContent>
    </Card>
  );
}
